import React from 'react';
import CountdownTimer from './components/CountdownTimer';
import type { PixPaymentViewProps } from './types/pix-payment.types';
import { usePixPayment } from './hooks/usePixPayment';
import PixHeader from './components/PixHeader';
import PixQrCodeSection from './components/PixQrCodeSection';
import PixEmvSection from './components/PixEmvSection';
import PixInstructions from './components/PixInstructions';

const PixPaymentView: React.FC<PixPaymentViewProps> = (props) => {
  const { emvCopied, handleCopyEmv } = usePixPayment(props);
  const hasHostedCheckout =
    typeof props.paymentDetails.checkoutUrl === 'string' &&
    props.paymentDetails.checkoutUrl.trim().length > 0 &&
    !props.paymentDetails.emv &&
    !props.paymentDetails.qrCode;

  const handleViewOrderDetails = () => {
    // Redirecionar diretamente para a homepage da loja
    // Tenta homePage primeiro, depois corsUrl como fallback
    if (props.shopifyData?.store?.homePage) {
      window.location.href = props.shopifyData.store.homePage;
    } else if (props.shopifyData?.store?.corsUrl) {
      window.location.href = props.shopifyData.store.corsUrl;
    } else {
      console.warn('Homepage ou corsUrl da loja não encontrados');
    }
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8 max-w-3xl bg-card-bg-light dark:bg-card-bg-dark shadow-xl rounded-lg my-8">
      <PixHeader 
        customerName={props.customerName}
        totalAmount={props.totalAmount}
      />

      {props.paymentDetails.expirationDate && (
        <div className="text-center mb-6">
          <p className="text-lg font-medium text-text-primary dark:text-gray-100">
            Tempo restante para pagamento:
          </p>
          <CountdownTimer
            expirationDate={props.paymentDetails.expirationDate}
            className="text-2xl font-bold text-red-600 dark:text-red-500"
          />
        </div>
      )}

      {hasHostedCheckout ? (
        <div className="mb-8 rounded-xl border border-emerald-200 bg-emerald-50/70 p-6 text-center dark:border-emerald-900 dark:bg-emerald-950/20">
          <p className="text-lg font-semibold text-text-primary dark:text-gray-100">
            Finalize o PIX no checkout seguro da {props.paymentDetails.provider === 'noxpay' ? 'NoxPay' : 'operadora'}.
          </p>
          <p className="mt-2 text-sm text-text-secondary dark:text-gray-400">
            Você será redirecionado para uma página hospedada com o QR Code e a confirmação do pagamento.
          </p>
          <a
            href={props.paymentDetails.checkoutUrl}
            className="mt-5 inline-flex items-center justify-center rounded-lg bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
          >
            Abrir checkout PIX
          </a>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row items-center justify-center lg:space-x-8 mb-8">
          <PixQrCodeSection qrCode={props.paymentDetails.qrCode} />

          <div className="flex flex-col items-center lg:items-start">
            <PixEmvSection
              emv={props.paymentDetails.emv}
              onCopyEmv={handleCopyEmv}
              emvCopied={emvCopied}
            />
          </div>
        </div>
      )}

      {/* Seção independente para instruções */}
      <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
        <PixInstructions />
      </div>

      <div className="mt-8 text-center text-sm text-text-secondary dark:text-gray-400">
        <p>Após a confirmação do pagamento, você receberá um e-mail com os detalhes do seu pedido.</p>
      </div>

      {/* Botão para ver detalhes do pedido */}
      <div className="mt-6 text-center">
        <div className="mb-3 text-lg text-text-secondary dark:text-gray-400">
          Continuar para a loja após finalizar o pagamento
        </div>
        <button
          onClick={handleViewOrderDetails}
          className="text-white font-semibold py-3 px-6 rounded-lg transition duration-150 text-base btn-primary"
        >
          Continuar à Loja
        </button>
      </div>
    </div>
  );
};

export default PixPaymentView; 